<template>
  <app-content class="report-view">
    <template #breadcrumb-custom>
     <b-row align-h="between" class="no-internal-print">
       <b-btn variant="link" @click="showHelpModal" class="p-0">
        <i
          aria-hidden="true"
          class="fas fa-info-circle variables-and-reference__tooltip-icon--blue"
          id="variables-and-reference__tooltip"
        />
        <b-tooltip
          placement="right"
          boundary="viewport"
          target="variables-and-reference__tooltip"
        >
          <span>
            {{ $t('reports.alarms-and-warnings.tooltips.alarm-functioning') }}.
          </span>
        </b-tooltip>
      </b-btn>
      <b-row
        class="mx-3"
      >
        <excel-export-button
          class="mr-2"
          @click="$event.wait(exportExcel())"
        />
        <b-button
          variant="outline-primary"
          :disabled="loading"
          @click="print"
        >
          {{ $t('buttons.print') }}
        </b-button>
      </b-row>
     </b-row>
    </template>
    <paged-report-panel
      v-model="pagedFilter"
      :total-rows="totalRecords"
      :loading="loading"
      :search-term-property="'genericSearch'"
      @input="onRefreshSearch"
    >
      <b-container class="p-0" fluid>
        <b-row align-h="end">
          <b-col class="pl-0" cols="auto">
            <collapse-expand-dropdown
              :disabled="!history.length"
              @click-option="showAllHandler"
            />
          </b-col>
        </b-row>
        <b-row class="report-view__table">
          <b-col class="align-center report-view__table-item">
            <s-loader v-if="loading" size="lg" class="h-100" />
            <template v-else-if="history.length">
              <div v-for="(data, index) in history" :key="index">
                <accordion-panel
                  class="mb-3"
                  id="alarm-history"
                  :collapsed.sync="data.collapsed"
                >
                  <b-row class="pl-3 report-view__header-wrapper" slot="title">
                    <b-col
                      xs="12"
                      md="5"
                      class="report-view__headers-alarm-name"
                    >
                      <span>
                        <icon-badge
                          class="report-view__icon-badge"
                          icon-class="fas fa-bell"
                          badge-icon-class="fas fa-check"
                          :icon-color="getAlarmColor(data)"
                          :badge-color="getBadgeColor(data)"
                          :show-badge="isAlarmRecognized(data)"
                          :tooltip-message="getTooltipMessageFromAlarm(data)"
                          :outlined="false"
                        />
                      </span>
                      <span>
                        <label class="mb-0 ml-3">
                          {{ getModuleDisplayName(data.moduleName) }} - {{ data.alarmName }}
                        </label>
                      </span>
                    </b-col>
                    <b-col
                      xs="12"
                      md="7"
                      class="report-view__description-wrapper"
                    >
                      <span class="report-view__icon-value">
                        <svg class="report-view__icon mr-1">
                          <use
                            xlink:href="static/imgs/building.svg#building-svg"
                          />
                        </svg>
                        {{ getCompanyAndRegionalName(data) }}
                      </span>
                      <span class="report-view__icon-value">
                        <svg class="report-view__icon mr-1">
                          <use
                            xlink:href="static/imgs/electrical-network-dynamic
                            .svg#electrical-network-svg"
                          />
                        </svg>
                        {{ data.installationName }}
                      </span>
                      <span class="report-view__icon-value">
                        <svg class="report-view__icon mr-1">
                          <use
                            xlink:href="static/imgs/transformer-dynamic.svg#transformer-svg"
                          />
                        </svg>
                        {{ data.assetName }}
                      </span>
                      <span
                        v-if="data.serialNumber"
                        class="report-view__icon-value"
                      >
                        <svg class="report-view__icon mr-1">
                          <use
                            xlink:href="static/imgs/bar-code.svg#barcode-svg"
                          />
                        </svg>
                        {{ data.serialNumber }}
                      </span>
                      <span class="report-view__classification">
                        <classification :classification="data.classification" />
                      </span>
                      <b-button
                        class="report-view__details-btn"
                        variant="link"
                        @click="openDetailsModal(data)"
                        style="min-width: 110px;"
                      >
                        {{ $t('alarms-and-warnings.see-details') }}
                        <i
                          class="fas fa-arrow-right report-view__details-btn__details-arrow"
                        />
                      </b-button>
                    </b-col>
                  </b-row>
                  <div v-if="data.offlineAlarm">
                    <div class="report-view__offline-alarm-infos">
                      <div>
                        <strong>{{
                          $t('reports.alarms-and-warnings.alarm.lot')
                        }}</strong>
                        <span class="ml-2">{{
                          data.lot | emptyValueFallbackFilter
                        }}</span>
                      </div>
                      <div>
                        <strong>{{
                          $t('reports.alarms-and-warnings.alarm.sampling')
                        }}</strong>
                        <span class="report-view__date-time ml-2">
                          {{
                            formatSampleDateTime(data.sampleDateTime)
                              | emptyValueFallbackFilter
                          }}
                        </span>
                      </div>
                    </div>
                    <div class="report-view__offline-alarm-action-infos">
                      <div>
                        <strong>{{
                          $t('reports.alarms-and-warnings.alarm.recognition')
                        }}</strong>
                        <span class="report-view__date-time ml-2">
                          {{ getFormattedDateTime(data.recognizeDate) }}
                        </span>
                      </div>
                      <div>
                        <strong>{{
                          $t('reports.alarms-and-warnings.alarm.recognized-by')
                        }}</strong>
                        <span class="ml-2">{{
                          data.recognizedBy | emptyValueFallbackFilter
                        }}</span>
                      </div>
                      <div>
                        <strong>{{
                          $t('reports.alarms-and-warnings.alarm.ignored-on')
                        }}</strong>
                        <span class="report-view__date-time ml-2">
                          {{
                            getFormattedDateTime(data.ignoredAt)
                              | emptyValueFallbackFilter
                          }}</span
                        >
                      </div>
                      <div>
                        <strong>{{
                          $t('reports.alarms-and-warnings.alarm.ignored-by')
                        }}</strong>
                        <span class="ml-2">{{
                          data.ignoredBy | emptyValueFallbackFilter
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="report-view__alarm-infos">
                    <div>
                      <strong>{{
                        $t('reports.alarms-and-warnings.alarm.date-hour')
                      }}</strong>
                      <span class="report-view__date-time ml-2">
                        {{ getFormattedDateTime(data.entryDate) }}
                      </span>
                    </div>
                    <div>
                      <strong>{{
                        $t('reports.alarms-and-warnings.alarm.normalization')
                      }}</strong>
                      <span class="report-view__date-time ml-2">
                        {{ getFormattedDateTime(data.normalizeDate) }}
                      </span>
                    </div>
                    <div>
                      <strong>{{
                        $t('reports.alarms-and-warnings.alarm.recognition')
                      }}</strong>
                      <span class="report-view__date-time ml-2">
                        {{ getFormattedDateTime(data.recognizeDate) }}
                      </span>
                    </div>
                    <div>
                      <strong>{{
                        $t('reports.alarms-and-warnings.alarm.recognized-by')
                      }}</strong>
                      <span class="ml-2">{{
                        data.recognizedBy | emptyValueFallbackFilter
                      }}</span>
                    </div>
                  </div>
                  <div class="report-view__alarm-description-wrapper">
                    <strong>{{
                      $t('reports.alarms-and-warnings.alarm.comment')
                    }}</strong>
                    <span class="ml-2">{{
                      data.description | emptyValueFallbackFilter
                    }}</span>
                  </div>
                  <additional-fields-accordion
                    v-show="showAdditionalFields"
                    class="report-view__additional-fields"
                    :fields-to-remove="additionalFieldsToHideFromAccordion"
                    :id="index"
                    :data="data"
                    :expanded.sync="additionalFieldsExpanded[index]"
                  />
                </accordion-panel>
              </div>
            </template>
            <s-empty-list v-else />
          </b-col>
        </b-row>
      </b-container>
    </paged-report-panel>
    <help-modal ref="helpModal" />
    <details-modal ref="detailsModal" :alarm="modalAlarm" :okOnly="true" />
  </app-content>
</template>

<script>
import getModuleDisplayName from '@/services/moduleDisplayName';
import { AlarmStatusType } from 'sigma-mappings';
import PagedReportPanel from '@/domain/components/generalPanel/reports/PagedReportPanel';
import loadingMixin from '@/services/loadingMixin';
import storageManager from '@/services/sessionStorageManager';
import AccordionPanel from '@/components/AccordionPanel';
import IconBadge from '@/components/icon/IconBadge';
import {
  formatter as dateTimeFormatter,
  isValid,
} from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';
import AdditionalFieldsAccordion from '@/domain/components/generalPanel/reports/additionalFields/AdditionalFieldsAccordion';
import CollapseExpandDropdown from '@/components/buttons/CollapseExpandDropdown';
import Classification from '@/components/Classification';
import emptyValueFallbackFilter from '@/filters/emptyValueFallbackFilter';
import HelpModal from '@/app/generalPanel/reports/alarmsAndWarnings/helpModal';
import DetailsModal from '@/domain/components/generalPanel/alarmsAndWarnings/detailsAlarmModal';
import AlarmsWarningsService from '@/domain/services/generalPanel/alarmsAndWarnings/general';
import print from '@/services/print';
import ExcelExportButton from '@/components/buttons/ExcelExportButton';
import AlarmsAndWarningsService from '@/domain/services/generalPanel/reports/alarmsAndWarnings';

export default {
  components: {
    AccordionPanel,
    AdditionalFieldsAccordion,
    CollapseExpandDropdown,
    ExcelExportButton,
    IconBadge,
    PagedReportPanel,
    Classification,
    HelpModal,
    DetailsModal,
  },
  mixins: [loadingMixin],
  data: () => ({
    pagedFilter: {
      pageSize: 15,
      page: 1,
      filter: {},
    },
    totalRecords: 0,
    isExpanded: true,
    additionalFieldsExpanded: [],
    additionalFieldsToHideFromAccordion: ['serialNumber'],
    history: [],
    successMessageShowed: false,
    modalAlarm: { eventos: [] },
  }),
  computed: {
    showAdditionalFields() {
      const additionalFieldsSelected = this.pagedFilter.filter.additionalFields;
      const onlySerialNumberSelected = (additionalFieldsSelected || []).length === 1
        && additionalFieldsSelected[0] === 'serialNumber';
      return (
        (additionalFieldsSelected || []).length && !onlySerialNumberSelected
      );
    },
  },
  filters: {
    emptyValueFallbackFilter,
  },
  created() {
    this.getSessionStorage();
    this.refreshData();
    this.selectExpandAll();
  },
  methods: {
    getModuleDisplayName,
    print,
    exportExcel() {
      return AlarmsAndWarningsService.exportHistoryExcel({ filter: this.pagedFilter.filter })
        .then(() => this.$pushAlert({ code: 'MS011' }));
    },
    getCompanyAndRegionalName(data) {
      if (data.regionalName) {
        return `${data.companyName}/(${data.regionalName})`;
      }

      return data.companyName;
    },
    formatSampleDateTime(value) {
      if (isValid(value)) {
        return this.getFormattedSampleDateTime(value);
      }
      return value;
    },
    getFormattedDateTime(date) {
      return dateTimeFormatter(
        date,
        formats.DATETIME_SHORT_WITH_SECONDS,
        null,
        '-',
      );
    },
    getFormattedSampleDateTime(date) {
      return dateTimeFormatter(
        date,
        formats.DATETIME_SHORT_WITH_SECONDS,
        timezones.UTC,
        '-',
      );
    },
    getTooltipMessageFromAlarm(alarm) {
      const active = this.isAlarmActive(alarm);
      const recognized = this.isAlarmRecognized(alarm);
      const phrasesRules = {
        'acted-not-recognized': active && !recognized,
        'acted-recognized': active && recognized,
        'not-acted-not-recognized': !active && !recognized,
        'not-acted-recognized': !active && recognized,
      };
      const phrases = Object.keys(phrasesRules);
      const result = phrases.find(phrase => !!phrasesRules[phrase]);
      return this.$t(`reports.alarms-and-warnings.tooltips.${result}`);
    },
    isAlarmActive(alarm) {
      return alarm.alarmCondition === AlarmStatusType.ACTED_ALARM;
    },
    isAlarmRecognized(alarm) {
      return alarm.alarmRecognition === AlarmStatusType.RECOGNIZED_ALARM;
    },
    getAlarmColor(alarm) {
      const active = this.isAlarmActive(alarm);
      return active ? 'var(--color-secondary)' : 'var(--font-color-primary)';
    },
    getBadgeColor(alarm) {
      const active = this.isAlarmActive(alarm);
      return active ? 'var(--color-info)' : 'var(--font-color-primary)';
    },
    expandAdditionalFields() {
      this.additionalFieldsExpanded = this.history.map(() => true);
    },
    collapseAdditionalFields() {
      this.additionalFieldsExpanded = this.history.map(() => false);
    },
    showAllHandler(toExpand) {
      this.isExpanded = toExpand;
      if (toExpand) {
        this.selectExpandAll();
        return;
      }
      this.selectRetractAll();
    },
    selectExpandAll() {
      this.expandAdditionalFields();
      this.history = this.mapWithCollapsed(this.history, false);
    },
    selectRetractAll() {
      this.collapseAdditionalFields();
      this.history = this.mapWithCollapsed(this.history, true);
    },
    isRetractAllSelected() {
      return !this.isExpanded;
    },
    onRefreshSearch(input) {
      this.pagedFilter = input;
      this.refreshData();
    },
    checkIfShowSuccessMessage() {
      if (!this.successMessageShowed) {
        this.showSuccessMessage();
      }
    },
    mapWithCollapsed(array, collapsed) {
      return array.map(data => ({
        ...data,
        collapsed,
      }));
    },
    refreshData() {
      this.startLoading();
      AlarmsAndWarningsService.getAlarmHistory(this.pagedFilter)
        .then(response => {
          if (response.partial) {
            this.$pushAlert({ code: 'ME096' });
            return;
          }
          this.totalRecords = response.total;
          this.history = this.mapWithCollapsed(
            response.data,
            this.isRetractAllSelected(),
          );
          if (response.data.length === 0) {
            this.$pushAlert({ code: 'MA009' });
            return;
          }
          this.checkIfShowSuccessMessage();
          this.successMessageShowed = true;
        })
        .finally(() => this.endLoading());
    },
    getSessionStorage() {
      if (!this.$route.query) return;
      const storageItem = storageManager.getItem(this.$route.query.filter);
      this.pagedFilter.filter = {
        ...this.tryJsonParse(storageItem),
        ...this.pagedFilter.filter,
      };
    },
    tryJsonParse(item) {
      try {
        return JSON.parse(item);
      } catch (e) {
        this.$pushAlert({
          message: this.$t('reports.filter-error'),
          type: 'warning',
        });
        return {};
      }
    },
    showSuccessMessage() {
      this.$pushAlert({ code: 'MS011' });
    },
    showHelpModal() {
      this.$refs.helpModal.show();
    },
    async openDetailsModal(data) {
      this.modalAlarm = {
        id: data.alarmId,
        ...(await AlarmsWarningsService.getAlarmDetails(data.alarmId)),
        events: await AlarmsWarningsService.getAlarmEvents(data.alarmId),
      };
      this.$refs.detailsModal.show();
    },
  },
};
</script>
<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.report-view
  +media-xs
    font-size: .9rem
  &__icon
    color: var(--font-color-primary)
    display: inline-block
    width: 1rem
    height: 1rem
  &__additional-fields
    margin-top: 5px
    +media-min-xl
      margin-left: 50px
  &__table
    overflow-y: scroll
    height: calc(100vh - 265px)
    +media-max-sm
      &-item
        padding: 0
  &__header-wrapper
    +media-max-sm
      flex-direction: column
  &__headers-alarm-name
    display: inline-flex
    align-items: center
    padding-left: 0
    margin-bottom: 1rem
    +media-xs
      flex-direction: column
  &__icon-badge
    z-index: 1
  &__description-wrapper
    display: flex
    align-items: center
    align-content: flex-start
    flex-wrap: wrap
    justify-content: space-between
  &__classification
    white-space: nowrap
    display: inline-block
  &__icon-value
    align-items: center
    display: inline-flex
    margin-right: 20px
  &__alarm-key
    font-weight: bold
  &__margin-col
    margin-right: 12px
    +media-max-sm
      margin: 0
  &__offline-alarm-infos, &__offline-alarm-action-infos
    display: grid
    grid-gap: 10px
    grid-template-columns: repeat(4, 300px)
    margin: 0 0 5px 50px
    +media-max-sm
      margin-left: 0
      grid-template-columns: auto
    +media-md
      margin-left: 0
      grid-template-columns: repeat(2, 50%)
    +media-lg
      margin-left: 0
      grid-template-columns: repeat(3, auto)
  &__alarm-description-wrapper
    display: flex
    +media-min-xl
      margin-left: 50px
    +media-max-sm
      margin-left: 50px
      margin-top: 10px
    span
      word-break: break-all
  &__alarm-infos
    display: grid
    grid-gap: 10px
    grid-template-columns: repeat(3, auto)
    margin: 0 0 5px 50px
    +media-max-sm
      grid-template-columns: auto
    +media-md
      margin-left: 0
      grid-template-columns: repeat(2, 50%)
    +media-lg
      margin-left: 0
      grid-template-columns: repeat(4, auto)
    +media-min-2xl
      div
        min-width: 140px
  &__date-time
    white-space: nowrap
  ::v-deep.accordion-panel__title-container
    width: 100%
  ::v-deep.app-content__main-content
    padding: 20px 20px 20px 15px
  &__details-btn
    z-index: 2
    color: var(--color-secondary) !important
    font-size: 1em
    &__details-arrow
      margin-left: 5px
      transform: rotate(-45deg)
      font-size: 0.9em
      vertical-align: middle
  +media-max-sm
    ::v-deep.accordion-panel__right-controls
      width: 30px
    ::v-deep.accordion-panel__title-container
      max-width: calc(100% - 30px)
</style>
